import { locatedAnchor } from '../../../shared/routeAnchor';
import { locatedStop } from '../../../shared/routeRetention';
import type { Signal } from '../../../shared/types';
import type { RouteStop, StoredRoute } from '../data/corridor';
import type { LeadLocation } from './followupRoute';
import { orderStops } from './route';
import { locationOf, signalAddress, signalEventLabel } from './signals';

export { locationOf };

/** "Add + plan" (D74): a new business as a stop on the day's route, carrying its lead and the why, slotted
 *  nearest-first from the route's own anchor; and the stop taken back on undo. Pure; the route itself
 *  is the account's day route (D84), kept on the device for offline and queued to the server. */

export const stopForSignal = (
  s: Signal,
  leadId: string,
  at: LeadLocation,
  why: string | null,
): RouteStop => ({
  place_id: at.place_id ?? `signal:${s.id}`,
  name: s.business_name,
  address: at.address ?? signalAddress(s),
  lat: at.lat,
  lng: at.lng,
  types: [],
  detected_pos: s.detected_pos ?? 'unknown',
  lead_id: leadId,
  signal_id: s.id,
  note: why ?? [signalEventLabel(s), s.detail].filter(Boolean).join(' · '),
});

/** The route with the stop in it: a new route from the anchor when there is none, else the stops in
 *  nearest-first order from the route's own anchor with the new one among them (a repeat replaces). */
export const appendStop = (
  route: StoredRoute | null,
  stop: RouteStop,
  anchor: StoredRoute['anchor'],
  createdAt: string,
): StoredRoute => {
  const base: StoredRoute = route ?? { anchor, stops: [], built: true, created_at: createdAt };
  const merged = [...base.stops.filter((s) => s.place_id !== stop.place_id), stop];
  // An expired stop still carries the rep's activity references; adding a door must not erase it.
  const start = locatedAnchor({ ...base, stops: merged });
  if (!start || merged.some((s) => !locatedStop(s))) return { ...base, stops: merged, built: true };
  const stops = orderStops(start, [
    ...base.stops.filter((s) => s.place_id !== stop.place_id),
    stop,
  ].filter(locatedStop)).map((o) => o.stop);
  return { ...base, stops, built: true };
};

export const removeStop = (route: StoredRoute, placeId: string): StoredRoute => ({
  ...route,
  stops: route.stops.filter((s) => s.place_id !== placeId),
});
